<script setup lang="ts">
import { computed, ref, watch, type CSSProperties } from 'vue';
import {
  ContractManagementColumnExtractionScope,
  type ExtractionPromptInDb,
  type ExtractionPromptUpdate,
  type ExtractionPromptVersionInDb,
} from '@/api';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCard from '@/components/design/base/GaiaCard.vue';
import GaiaCollapse from '@/components/design/base/GaiaCollapse.vue';
import GaiaCountPill from '@/components/design/base/GaiaCountPill.vue';
import GaiaDrawer from '@/components/design/base/GaiaDrawer.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaPanel from '@/components/design/composite/GaiaPanel.vue';
import GaiaTimeline, {
  type GaiaTimelineItem,
} from '@/components/design/composite/GaiaTimeline.vue';
import { IconClose } from '@/components/design/icons';
import { gaiaShellPadding, gaiaTextButtonOverrides } from '@/components/design/themes/tokens';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { ExtractionPromptsService } from '@/services/contractManagement/extractionPromptsService';
import { useContractTypeStore } from '@/stores/contractTypeStore';
import {
  enumOptionsError,
  formToQuestion,
  promptToForm,
  responseTypeLabel,
  sameQuestion,
  type QuestionFormFields,
} from '@/utils/contractManagementView/columnQuestion';
import { formatDateTimeForTable } from '@/utils/datetimeUtils';
import { logger } from '@/utils/logger';
import ExtractionScopeModal from './ExtractionScopeModal.vue';
import QuestionFields from './QuestionFields.vue';

/** Create an organisation extraction prompt, or edit one in place (org admins only). */
const props = withDefaults(
  defineProps<{
    show: boolean;
    /** The prompt to edit; null creates one. */
    prompt?: ExtractionPromptInDb | null;
    /** A new prompt's contract types to start with. */
    defaultContractTypeUids?: string[];
    /**
     * Render as a side panel for Layout's `#drawer` slot instead of an overlay drawer, so the
     * page beside it stays in view; the page opens and closes the slot with `show`.
     */
    inline?: boolean;
  }>(),
  { prompt: null, defaultContractTypeUids: () => [], inline: false }
);

const emit = defineEmits<{
  'update:show': [value: boolean];
  saved: [prompt: ExtractionPromptInDb];
}>();

const message = useGaiaToast();
const contractTypeStore = useContractTypeStore();

const isEdit = computed(() => props.prompt !== null);

type PromptForm = QuestionFormFields & {
  /** The internal name. */
  name: string;
  /** What users see; empty uses the name. */
  displayName: string;
  contractTypeUids: string[];
};

const form = ref<PromptForm>(emptyForm());
const nameError = ref('');
const promptError = ref('');
const saving = ref(false);
const showScopeModal = ref(false);

function emptyForm(): PromptForm {
  return {
    name: '',
    displayName: '',
    prompt: '',
    responseType: 'str',
    exampleData: '',
    enumOptions: [],
    contractTypeUids: [...props.defaultContractTypeUids],
  };
}

// ==================== History ====================

/** The edited prompt's questions, newest version first. */
const versions = ref<ExtractionPromptVersionInDb[]>([]);
/** The history's collapse item while open; collapsed for every prompt opened. */
const historyExpanded = ref<string[]>([]);
let latestVersionsRequest = 0;

async function loadVersions(uid: string): Promise<void> {
  const request = ++latestVersionsRequest;
  try {
    const result = await ExtractionPromptsService.searchVersions(uid);
    if (request === latestVersionsRequest) versions.value = result;
  } catch (error) {
    // The history is extra: without it the prompt can still be edited.
    logger.error('Failed to load the prompt versions:', error);
  }
}

const historyItems = computed((): GaiaTimelineItem[] =>
  versions.value.map(version => ({
    key: version.uid,
    label: `Version ${version.version}`,
    time: formatDateTimeForTable(version.created_at),
    done: version.is_current,
  }))
);

const versionsByUid = computed(
  (): Map<string, ExtractionPromptVersionInDb> =>
    new Map(versions.value.map(version => [version.uid, version]))
);

function versionPrompt(uid: string): string {
  return versionsByUid.value.get(uid)?.prompt ?? '';
}

function versionTypeLabel(uid: string): string {
  const version = versionsByUid.value.get(uid);
  return version ? responseTypeLabel(version.response_type) : '';
}

/** The form already asks this version's question, so restoring it changes nothing. */
function isVersionInForm(uid: string): boolean {
  const version = versionsByUid.value.get(uid);
  return version ? sameQuestion(form.value, version) : false;
}

/** Load an older question into the form; saving makes it the next version. */
function restoreVersion(uid: string): void {
  const version = versionsByUid.value.get(uid);
  if (!version) return;
  Object.assign(form.value, promptToForm(version));
  promptError.value = '';
}

// Also on a new prompt while open: the side panel stays open when another row is edited.
watch([(): boolean => props.show, (): ExtractionPromptInDb | null => props.prompt], ([show]) => {
  if (!show) return;
  void contractTypeStore.ensureLoaded();
  nameError.value = '';
  promptError.value = '';
  form.value = props.prompt
    ? {
        ...promptToForm(props.prompt),
        name: props.prompt.name,
        displayName: props.prompt.display_name,
        contractTypeUids: (props.prompt.contract_types ?? []).map(type => type.uid),
      }
    : emptyForm();
  versions.value = [];
  historyExpanded.value = [];
  if (props.prompt) void loadVersions(props.prompt.uid);
});

function close(): void {
  emit('update:show', false);
}

function validate(): boolean {
  nameError.value = form.value.name.trim() ? '' : 'Please enter a name';
  promptError.value = form.value.prompt.trim() ? '' : 'Please enter a prompt or question';
  if (nameError.value || promptError.value) return false;
  const optionsError = enumOptionsError(form.value);
  if (optionsError) {
    message.error(optionsError);
    return false;
  }
  return true;
}

async function handleSave(): Promise<void> {
  if (!validate()) return;
  if (props.prompt && !sameQuestion(form.value, props.prompt)) {
    // A changed question re-extracts the columns asking it, so ask how much first.
    showScopeModal.value = true;
    return;
  }
  await save(ContractManagementColumnExtractionScope.NEW_ONLY);
}

/** *scope* only matters when the question changed; otherwise nothing is re-extracted. */
async function save(scope: ContractManagementColumnExtractionScope): Promise<void> {
  saving.value = true;
  try {
    const name = form.value.name.trim();
    const displayName = form.value.displayName.trim();
    let saved: ExtractionPromptInDb;
    if (props.prompt) {
      const body: ExtractionPromptUpdate = {
        name,
        // Left out when unchanged: the backend then renames it with the name while it still is
        // the old name. Never null: an empty display name falls back to the name, as on create.
        ...(displayName !== props.prompt.display_name && { display_name: displayName || name }),
        contract_type_uids: form.value.contractTypeUids,
        ...(!sameQuestion(form.value, props.prompt) && formToQuestion(form.value)),
      };
      saved = await ExtractionPromptsService.update(props.prompt.uid, body, scope);
    } else {
      saved = await ExtractionPromptsService.create({
        ...formToQuestion(form.value),
        name,
        // Left out when empty: the backend then uses the name.
        ...(displayName && { display_name: displayName }),
        contract_type_uids: form.value.contractTypeUids,
      });
    }
    message.success(isEdit.value ? 'Prompt updated' : 'Prompt created');
    emit('saved', saved);
    close();
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to save the prompt');
  } finally {
    saving.value = false;
  }
}

const title = computed(() => (isEdit.value ? 'Edit prompt' : 'Add prompt'));

/** The side panel's insets, as AddExistingPromptsPanel's: the page's top padding, a form's sides. */
const panelStyle: CSSProperties = {
  paddingTop: gaiaShellPadding.top,
  paddingLeft: '24px',
  paddingRight: '24px',
};

async function handleConfirmScope(scope: ContractManagementColumnExtractionScope): Promise<void> {
  showScopeModal.value = false;
  await save(scope);
}
</script>

<template>
  <component
    :is="inline ? GaiaPanel : GaiaDrawer"
    v-bind="
      inline
        ? { style: panelStyle }
        : {
            show,
            width: 600,
            title,
            'onUpdate:show': (value: boolean) => (value ? undefined : close()),
          }
    "
  >
    <!-- The overlay drawer draws its own title and close button; the panel doesn't. -->
    <template v-if="inline" #header>
      <n-flex align="flex-start" :size="12" :wrap="false" :style="{ width: '100%' }">
        <gaia-text tag="div" size="subtitle" weight="semibold" tone="ink" :style="{ minWidth: 0 }">
          {{ title }}
        </gaia-text>
        <gaia-icon-button
          :dimension="28"
          quaternary
          title="Close"
          :disabled="saving"
          :style="{ marginLeft: 'auto', flex: 'none' }"
          @click="close"
        >
          <n-icon :component="IconClose" :size="14" />
        </gaia-icon-button>
      </n-flex>
    </template>

    <n-flex vertical :size="16" :wrap="false">
      <gaia-form-field label="Display name">
        <gaia-input
          v-model:value="form.displayName"
          placeholder="e.g. Change of Control (Services)"
          :maxlength="200"
          style="width: 100%"
        />
      </gaia-form-field>

      <gaia-form-field label="Name" required :error="nameError || undefined">
        <gaia-input
          v-model:value="form.name"
          placeholder="e.g. Contract value, Start date"
          :maxlength="200"
          :status="nameError ? 'error' : undefined"
          style="width: 100%"
          @update:value="nameError = ''"
        />
      </gaia-form-field>

      <question-fields v-model:prompt-error="promptError" :form="form" />

      <gaia-form-field label="Contract types">
        <gaia-select
          v-model:value="form.contractTypeUids"
          multiple
          filterable
          :max-tag-count="3"
          :options="contractTypeStore.uidOptions"
          placeholder="No contract type"
        />
      </gaia-form-field>

      <!-- Only once the question has changed: a single version has no history to show. -->
      <gaia-collapse v-if="isEdit && versions.length > 1" v-model:expanded-names="historyExpanded">
        <n-collapse-item name="history">
          <template #header>
            <gaia-text size="dense" weight="semibold" tone="ink">Prompt history</gaia-text>
          </template>
          <template #header-extra>
            <gaia-count-pill>{{ versions.length }}</gaia-count-pill>
          </template>
          <gaia-timeline :items="historyItems">
            <template #item="{ item }">
              <gaia-card size="small" content-style="padding: 10px 12px">
                <n-flex align="center" :size="8" :wrap="false">
                  <gaia-text size="meta" weight="bold" tone="ink">{{ item.label }}</gaia-text>
                  <gaia-text size="meta" tone="faint">{{ item.time }}</gaia-text>
                  <gaia-tag variant="muted" size="tiny">
                    {{ versionTypeLabel(item.key) }}
                  </gaia-tag>
                  <gaia-tag v-if="item.done" variant="muted" size="tiny" style="margin-left: auto">
                    Current
                  </gaia-tag>
                  <gaia-button
                    v-else
                    text
                    size="tiny"
                    :theme-overrides="gaiaTextButtonOverrides.ink"
                    :disabled="isVersionInForm(item.key)"
                    style="margin-left: auto"
                    @click="restoreVersion(item.key)"
                  >
                    Restore
                  </gaia-button>
                </n-flex>
                <gaia-text
                  size="dense"
                  tone="tertiary"
                  :style="{
                    display: 'block',
                    marginTop: '6px',
                    lineHeight: 1.55,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }"
                >
                  {{ versionPrompt(item.key) }}
                </gaia-text>
              </gaia-card>
            </template>
          </gaia-timeline>
        </n-collapse-item>
      </gaia-collapse>
    </n-flex>

    <template #footer>
      <n-flex justify="end" :size="10">
        <gaia-button @click="close">Cancel</gaia-button>
        <gaia-button type="primary" :loading="saving" @click="handleSave">
          {{ isEdit ? 'Save' : 'Add prompt' }}
        </gaia-button>
      </n-flex>
    </template>
  </component>

  <extraction-scope-modal
    v-model:show="showScopeModal"
    :loading="saving"
    across-views
    @confirm="handleConfirmScope"
  >
    You changed this prompt's question. Every view column that asks it is re-extracted; which
    contracts should get the new answer?
  </extraction-scope-modal>
</template>
